import { ProjectId } from "@custom-types/base-types";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import { createSelector } from "@reduxjs/toolkit";
import { projectsMapAdapter } from "@store/projects-map/projects-map-slice";
import { RootState } from "@store/store-helper";
import { FetchingStatus } from "@store/store-types";
import { getProjectArchivingState } from "@utils/project-utils";

/**
 * @returns all map projects.
 */
const mapProjectsSelector: (state: RootState) => SdbProject[] = createSelector(
  (state: RootState) => state.projectsMap,
  (projectsMapState) => {
    return projectsMapAdapter.getSelectors().selectAll(projectsMapState);
  }
);

/**
 * @returns all map projects with the specified archiving state.
 * @param archivingState - The archiving state of the projects to filter by
 */
export function mapProjectsByArchivingStateSelector(
  archivingState: ProjectArchivingState
): (state: RootState) => SdbProject[] {
  return createSelector(mapProjectsSelector, (projects) => {
    return projects.filter((project) => {
      const projectArchivingState = getProjectArchivingState(
        project.archivingState
      );
      return projectArchivingState === archivingState;
    });
  });
}

/**
 * @returns the fetching status of the map projects
 */
export const mapProjectsFetchingStatusSelector: (
  state: RootState
) => FetchingStatus = createSelector(
  (state: RootState) => state.projectsMap,
  (projectsMapState) => projectsMapState.fetchingStatus
);

/**
 * @returns The project entity with the specified id, or null if it does not exist in the store
 * @param projectId - The ID of the project to select
 */
export function getMapProjectByIdSelector(
  projectId: ProjectId
): (state: RootState) => SdbProject | null {
  return createSelector(
    (state: RootState) => state.projectsMap,
    (projectsMapState) => {
      return (
        projectsMapAdapter
          .getSelectors()
          .selectById(projectsMapState, projectId) ?? null
      );
    }
  );
}
